import { useGetMessageTemplates, useGetProgramById } from '@frontend/app/hooks';
import { useAppContext } from '@frontend/context/AppContext';
import { RawDraftContentState } from 'draft-js';
import { useCallback, useMemo } from 'react';
import getInitialEmailState from '../utils/initialEmailStateSendOrderRequest';

interface DefaultEmailTemplate {
  isDefaultEmailTemplateLoading?: boolean;
  getDefaultEmailTemplate: () => [string, RawDraftContentState] | null;
}

interface IProps {
  programId: number;
}
export const useGetDefaultEmailTemplate = (props: IProps): DefaultEmailTemplate => {
  const { programId } = props;

  const { apolloClient } = useAppContext();

  const { templates, loading: messageTemplatesLoading } = useGetMessageTemplates({
    variables: {
      programId,
    },
    client: apolloClient,
    skip: !programId,
  });

  const { data: programData, loading: programLoading } = useGetProgramById({
    variables: {
      id: programId,
    },
    client: apolloClient,
    skip: !programId,
  });

  const defaultProductEmailTemplateId = programData?.program?.programMetadata?.defaultProductEmailTemplateId;

  const savedEmailTemplate = useMemo(() => {
    if (messageTemplatesLoading || programLoading || !defaultProductEmailTemplateId || !templates?.length) return null;

    const foundTemplate = templates.find(
      (template) => template.id === defaultProductEmailTemplateId
    );
    return foundTemplate;
  }, [templates, messageTemplatesLoading, programLoading, defaultProductEmailTemplateId]);

  const [defaultSubject, defaultText] = getInitialEmailState(
    programData?.program?.title,
    programId,
  );

  const isInitialEmailStateLoading = !defaultSubject || !defaultText;

  const getSavedEmailTemplate = useCallback<(() => [string, RawDraftContentState])>(() => {
    if (!savedEmailTemplate) return null;

    return [
      savedEmailTemplate.subject,
      JSON.parse(savedEmailTemplate.text),
    ];
  }, [savedEmailTemplate]);

  const getDefaultEmailTemplate = useCallback<(() => [string, RawDraftContentState])>(() => {
    return [
      defaultSubject,
      defaultText,
    ];
  }, [defaultSubject, defaultText]);

  const defaultEmailTemplate = savedEmailTemplate ? getSavedEmailTemplate : getDefaultEmailTemplate;

  return {
    isDefaultEmailTemplateLoading: messageTemplatesLoading || programLoading || isInitialEmailStateLoading,
    getDefaultEmailTemplate: defaultEmailTemplate,
  };
};
